/**
 * Replay a conversation's stored event stream into user and assistant messages.
 *
 * Split out of `conversations.ts`, which keeps the conversation API calls and
 * the shared types; this module owns rebuilding messages from `/events`.
 */

import type { MessageArtifact } from '@/stores/useAppStore';

import { useDocumentViewerStore } from '../chat/stores/useDocumentViewerStore';
import { stepsFromStored, type ActivityStep } from '../chat/utils/activitySteps';

import type {
  ConversationEvent,
  FileArtifactInfo,
  ProcessedConversation,
  ProcessedMessage,
  RestoredActivityFields,
  StoredCitation,
} from './conversations';

/** Build a ProcessedMessage from a user_message event. */
function buildUserMessage(event: ConversationEvent): ProcessedMessage {
  return {
    role: 'user',
    content: event.content ?? '',
    ...(event.timestamp && { timestamp: event.timestamp }),
    ...(event.message_id ? { message_id: event.message_id } : {}),
  };
}

interface AssistantAccumulator {
  buffer: string;
  time: string | undefined;
  interrupted: boolean;
  messageId: string | undefined;
  citations: StoredCitation[];
  fileArtifacts: FileArtifactInfo[];
  /** Document artifacts from document_created events in this turn. */
  documentArtifacts: MessageArtifact[];
  /** Render format captured from the first text_content event in the run. */
  format: 'markdown' | 'html' | undefined;
  /** Tool activity restored from an activity event for this turn. */
  activity: ActivityStep[];
  turnStartedAt: number | undefined;
  turnEndedAt: number | undefined;
  activityStepCount: number | undefined;
  /** Whether the stored activity record was shed to fit its ceiling. */
  truncated: boolean;
}

/**
 * Creates a fresh assistant accumulator with empty buffer, no citations,
 * and no file artifacts. Used to start accumulating a new assistant message.
 *
 * @returns A new `AssistantAccumulator` in its initial state.
 */
function createAssistantAccumulator(): AssistantAccumulator {
  return {
    buffer: '',
    time: undefined,
    interrupted: false,
    messageId: undefined,
    citations: [],
    fileArtifacts: [],
    documentArtifacts: [],
    format: undefined,
    activity: [],
    turnStartedAt: undefined,
    turnEndedAt: undefined,
    activityStepCount: undefined,
    truncated: false,
  };
}

/**
 * Returns true when the accumulator has content worth flushing into a message.
 *
 * @param acc - The accumulator to check.
 */
function accumulatorHasContent(acc: AssistantAccumulator): boolean {
  return (
    acc.buffer.trim().length > 0 ||
    acc.fileArtifacts.length > 0 ||
    acc.documentArtifacts.length > 0 ||
    // A turn can be nothing but tool calls — stopped mid-search, say — and
    // dropping it here is what made those turns vanish on reload.
    acc.activity.length > 0
  );
}

/**
 * Copy a turn's restored tool activity onto its message, when there is any.
 *
 * Split out of `buildAssistantMessage` to keep that function's branching
 * under the complexity ceiling.
 *
 * @param acc - The completed accumulator.
 * @param msg - The message being built; mutated in place.
 */
function applyActivityFields(acc: AssistantAccumulator, msg: ProcessedMessage): void {
  if (acc.activity.length === 0) return;
  msg.activity = acc.activity;
  if (acc.truncated) msg.activityTruncated = true;
  if (acc.turnStartedAt !== undefined) msg.turnStartedAt = acc.turnStartedAt;
  if (acc.turnEndedAt !== undefined) msg.turnEndedAt = acc.turnEndedAt;
  if (acc.activityStepCount !== undefined) msg.activityStepCount = acc.activityStepCount;
}

/**
 * Build a `ProcessedMessage` from a fully accumulated assistant turn.
 *
 * @param acc - The completed accumulator.
 */
function buildAssistantMessage(acc: AssistantAccumulator): ProcessedMessage {
  const msg: ProcessedMessage = { role: 'assistant', content: acc.buffer };
  if (acc.time) msg.timestamp = acc.time;
  if (acc.interrupted) msg.interrupted = true;
  if (acc.messageId) msg.message_id = acc.messageId;
  if (acc.citations.length > 0) msg.citations = acc.citations;
  if (acc.fileArtifacts.length > 0) msg.fileArtifacts = acc.fileArtifacts;
  if (acc.documentArtifacts.length > 0) msg.artifacts = acc.documentArtifacts;
  if (acc.format) msg.format = acc.format;
  applyActivityFields(acc, msg);
  return msg;
}

/**
 * Flushes the accumulated assistant content into a new `ProcessedMessage` and
 * resets the accumulator. Does nothing if the buffer is empty and there are
 * no file or document artifacts.
 *
 * @param acc - The current assistant accumulator to flush.
 * @param messages - The output array to push the flushed message into.
 */
function flushAssistant(acc: AssistantAccumulator, messages: ProcessedMessage[]): void {
  if (!accumulatorHasContent(acc)) return;
  messages.push(buildAssistantMessage(acc));
  Object.assign(acc, createAssistantAccumulator());
}

/**
 * Accumulates text content from a conversation event into the assistant accumulator.
 * Captures timestamp, message ID, interruption flag, and citations from the first
 * or most relevant event in the current assistant message.
 *
 * @param event - The conversation event containing text content.
 * @param acc - The accumulator to append the content to.
 */
function handleTextContent(event: ConversationEvent, acc: AssistantAccumulator): void {
  acc.buffer += event.content ?? '';
  if (!acc.time) acc.time = event.timestamp;
  if (!acc.messageId) acc.messageId = event.message_id;
  if (event.interrupted) acc.interrupted = true;
  if (event.citations?.length) acc.citations.push(...event.citations);
  // First text_content event in the run carries the conversation's format.
  if (!acc.format && event.format) acc.format = event.format;
}

/**
 * Adds a file artifact from a create_file conversation event to the assistant
 * accumulator. Skips events without an artifact ID.
 *
 * @param event - The conversation event containing file creation metadata.
 * @param acc - The accumulator to append the file artifact info to.
 */
function handleCreateFile(event: ConversationEvent, acc: AssistantAccumulator): void {
  if (!event.artifact_id) return;
  if (!acc.messageId) acc.messageId = event.message_id;
  acc.fileArtifacts.push({
    artifact_id: event.artifact_id,
    filename: event.filename ?? 'file',
    mime_type: event.mime_type ?? 'text/plain',
    size: event.size ?? 0,
  });
}

/**
 * Handles a document_created event during history replay: attaches a lightweight
 * document artifact to the accumulator and upserts the full body into the viewer
 * store so it is available if the viewer is opened. Skips events without an
 * artifact_id.
 *
 * @param event - The conversation event containing document metadata and body.
 * @param acc - The accumulator to append the document artifact to.
 */
function handleDocumentCreated(event: ConversationEvent, acc: AssistantAccumulator): void {
  if (!event.artifact_id) return;
  if (!acc.messageId) acc.messageId = event.message_id;

  useDocumentViewerStore.getState().upsertDocument({
    id: event.artifact_id,
    name: event.name ?? 'Document',
    kind: event.kind ?? 'markdown',
    language: event.language ?? null,
    body: event.body ?? '',
  });

  acc.documentArtifacts.push({
    id: event.artifact_id,
    type: 'document',
    thumbUrl: '',
    name: event.name ?? 'Document',
  });
}

/**
 * Handles a user_attachment event during history replay: appends the image to the
 * user message whose event group it belongs to. The server emits it directly after
 * that message's user_message event, so it attaches only when the last message is a
 * user message and no assistant turn has started accumulating since. Skips events
 * without an artifact_id or thumbnail URL.
 *
 * @param event - The user_attachment event.
 * @param acc - The assistant accumulator; content in it means the user turn has ended.
 * @param messages - The messages built so far.
 */
function handleUserAttachment(
  event: ConversationEvent,
  acc: AssistantAccumulator,
  messages: ProcessedMessage[]
): void {
  if (!event.artifact_id || !event.thumb_url) return;
  const last = messages[messages.length - 1];
  if (last?.role !== 'user' || accumulatorHasContent(acc)) return;
  const image: MessageArtifact = {
    id: event.artifact_id,
    type: 'image',
    thumbUrl: event.thumb_url,
    ...(event.orig_url ? { origUrl: event.orig_url } : {}),
    name: event.name ?? 'Image',
  };
  last.artifacts = [...(last.artifacts ?? []), image];
}

/**
 * Flush when a different stored message starts.
 *
 * The accumulator merges every consecutive assistant event into one message,
 * which silently joined two stored turns; with activity in play it would also
 * attach the second turn's steps to the first.
 *
 * @param event - The event about to be applied.
 * @param acc - The accumulator currently being built.
 * @param messages - The output array to push a flushed message into.
 */
function flushOnMessageBoundary(
  event: ConversationEvent,
  acc: AssistantAccumulator,
  messages: ProcessedMessage[]
): void {
  if (event.message_id && acc.messageId && event.message_id !== acc.messageId) {
    flushAssistant(acc, messages);
  }
}

/**
 * Restore one assistant turn's tool activity from its replay event.
 *
 * @param event - The activity event.
 * @param acc - The accumulator for the turn being rebuilt.
 */
function handleActivity(event: ConversationEvent, acc: AssistantAccumulator): void {
  acc.truncated = Boolean(event.truncated);
  acc.activity = stepsFromStored(event.steps ?? [], event.started_at_ms ?? 0);
  acc.turnStartedAt = event.started_at_ms;
  acc.turnEndedAt = event.ended_at_ms;
  acc.activityStepCount = event.step_count;
  if (!acc.time) acc.time = event.timestamp;
  if (!acc.messageId) acc.messageId = event.message_id;
  if (event.interrupted) acc.interrupted = true;
}

/**
 * Process raw conversation events into a list of user and assistant messages.
 *
 * Merges consecutive text_content and create_file events into single assistant
 * messages, preserving timestamps, interruption flags, citations, and file artifacts.
 *
 * @param events - The raw conversation event stream.
 * @returns A ProcessedConversation with the assembled messages array.
 */
export const processConversationEvents = (events: ConversationEvent[]): ProcessedConversation => {
  const messages: ProcessedMessage[] = [];
  const acc = createAssistantAccumulator();

  for (const event of events) {
    switch (event.type) {
      case 'user_message':
        flushAssistant(acc, messages);
        messages.push(buildUserMessage(event));
        break;
      case 'activity':
        flushOnMessageBoundary(event, acc, messages);
        handleActivity(event, acc);
        break;
      case 'text_content':
        flushOnMessageBoundary(event, acc, messages);
        handleTextContent(event, acc);
        break;
      case 'create_file':
        flushOnMessageBoundary(event, acc, messages);
        handleCreateFile(event, acc);
        break;
      case 'document_created':
        flushOnMessageBoundary(event, acc, messages);
        handleDocumentCreated(event, acc);
        break;
      case 'user_attachment':
        handleUserAttachment(event, acc, messages);
        break;
    }
  }
  flushAssistant(acc, messages);
  return { messages };
};

/**
 * Pick the restored-activity fields off a processed message, omitting any that
 * are absent so a spread never overwrites a field with `undefined`.
 *
 * The one copy both history adapters use — `toStoreMessages` (main chat) and
 * `adaptMessage` (panel chat). Those adapters drifted on field coverage when
 * each kept its own list, and a field that survives only one of them is
 * invisible on the other surface.
 *
 * @param msg - The processed message restored from history.
 * @returns Only the activity fields the message actually carries.
 */
export function pickActivityFields(msg: RestoredActivityFields): RestoredActivityFields {
  return {
    ...(msg.activity?.length ? { activity: msg.activity } : {}),
    ...(msg.turnStartedAt !== undefined ? { turnStartedAt: msg.turnStartedAt } : {}),
    ...(msg.turnEndedAt !== undefined ? { turnEndedAt: msg.turnEndedAt } : {}),
    ...(msg.activityStepCount !== undefined ? { activityStepCount: msg.activityStepCount } : {}),
    ...(msg.activityTruncated ? { activityTruncated: true } : {}),
  };
}
